// SOApprovalActions.jsx
// Mirrors POApprovalActions — state machine for SO staging drafts:
// Draft/Rejected → Request Approval → Pending (approver sees Approve/Decline)

import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Send,
  ThumbsUp,
  ThumbsDown,
  Edit,
  Trash2,
  Lock,
  Clock,
} from "lucide-react";
import { domain } from "../../../../security";

const SOApprovalActions = ({
  so, // the SO object (isStaging = true)
  currentUserId,
  refreshData,
  onEdit,
  onDelete,
  onApproveSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const isApprover = so.approverUserId === currentUserId;
  const status = so.status?.toLowerCase() || "";
  const isPending = status === "pending approval" || status === "pending";
  const isDraftOrRejected = status === "draft" || status === "rejected";

  // 1. Request Approval
  const handleRequestApproval = async () => {
    if (!window.confirm("Submit this Sales Order for approval?")) return;
    setLoading(true);
    try {
      await axios.put(`${domain}/api/SalesOrders/request-approval/${so.id}`);
      toast.success("Approval requested!");
      await refreshData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to request approval.");
    } finally {
      setLoading(false);
    }
  };

  // 2. Approve
  const handleApprove = async () => {
    if (!window.confirm("Approve this Sales Order?")) return;
    setLoading(true);
    try {
      await axios.put(`${domain}/api/SalesOrders/approve/${so.id}`);
      toast.success("Sales Order approved!");
      await onApproveSuccess()
      await refreshData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve.");
    } finally {
      setLoading(false);
    }
  };

  // 3. Decline/Reject
  const handleDecline = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reason for declining.");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${domain}/api/SalesOrders/decline/${so.id}`, {
        reason: rejectReason,
      });
      toast.success("Sales Order declined.");
      setShowRejectInput(false);
      setRejectReason("");
      await refreshData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to decline.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400 animate-pulse px-2">
        <Clock size={13} /> Processing...
      </div>
    );
  }

  // ── STATE: DRAFT / REJECTED — show Request Approval + Edit + Delete ──
  if (isDraftOrRejected) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={handleRequestApproval}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
          title="Request Approval"
        >
          <Send size={13} /> Request
        </button>
        <button
          onClick={() => onEdit(so)}
          className="p-1.5 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition"
          title="Edit"
        >
          <Edit size={14} />
        </button>
        <button
          onClick={() => onDelete(so.id, true)}
          className="p-1.5 text-gray-400 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:text-red-600 transition"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    );
  }

  // ── STATE: PENDING APPROVAL ──
  if (isPending) {
    // Approver sees Approve / Decline
    if (isApprover) {
      return (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleApprove}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition shadow-sm"
              title="Approve"
            >
              <ThumbsUp size={13} /> Approve
            </button>
            <button
              onClick={() => setShowRejectInput((v) => !v)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition"
              title="Decline"
            >
              <ThumbsDown size={13} /> Decline
            </button>
          </div>
          {showRejectInput && (
            <div className="flex items-center gap-1.5 mt-1">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for declining..."
                className="flex-1 text-xs border border-red-300 rounded-lg px-2 py-1.5 focus:ring-red-300 focus:border-red-400 outline-none"
              />
              <button
                onClick={handleDecline}
                disabled={!rejectReason.trim()}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
              >
                Confirm
              </button>
            </div>
          )}
        </div>
      );
    }

    // Non-approver: locked state
    return (
      <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg">
        <Lock size={12} />
        <span className="font-medium">Awaiting Approval</span>
      </div>
    );
  }

  // ── STATE: Approved or other — no staging actions ──
  return null;
};

export default SOApprovalActions;
